import {
  type ChildId,
  type ChildProfile,
  type ContentType,
  childIdSchema,
  DISTANCE_WARNING_CENTIMETRES,
  type Schedule,
  type ScreenTimeBudget,
} from '@kidshub/contracts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ChildLanguages } from '../children/child-languages.js';
import { apiClient } from '../lib/api-client.js';
import { toMinutes, toSeconds } from '../lib/duration.js';
import { toErrorKey } from '../lib/error-code.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import { Button, Card, Icon, Row, Stepper, Switch } from '../ui/kit.js';
import { OutcomeBlock } from './outcome-block.js';
import { ScheduleEditor } from './schedule-editor.js';
import { WeekStrip } from './week-strip.js';
import './child.css';

/**
 * One child's settings: screen time, hours, breaks, the camera and languages.
 *
 * Edits are held as a draft and sent in one `updateProfile` call. Saving each
 * stepper as it moves would push half-finished states to a tablet a child is
 * holding — a forty-minute video budget briefly inside a ten-minute day.
 *
 * Only the fields this screen owns are sent. The profile carries collections
 * and feature switches that belong to other screens, and a partial update that
 * echoes them back would let this screen overwrite work done elsewhere.
 */

/** Shown as steppers whether or not the profile carries them yet. */
const EDITABLE_TYPES: readonly ContentType[] = ['VIDEO', 'GAME'];

/** Every duration on this page moves in five-minute steps. */
const MINUTE_STEP = 5;

/** How long "saved" stays on screen after a save. A display delay, not a rule. */
const SAVED_NOTICE_MS = 2500;

type Draft = Pick<
  ChildProfile,
  | 'budget'
  | 'schedule'
  | 'movementBreaksEnabled'
  | 'activeViewingEnabled'
  | 'distanceWarningEnabled'
  | 'distanceWarning'
  | 'nativeLanguage'
  | 'foreignLanguages'
>;

const toDraft = (profile: ChildProfile): Draft => ({
  budget: profile.budget,
  schedule: profile.schedule,
  movementBreaksEnabled: profile.movementBreaksEnabled,
  activeViewingEnabled: profile.activeViewingEnabled,
  distanceWarningEnabled: profile.distanceWarningEnabled,
  distanceWarning: profile.distanceWarning,
  nativeLanguage: profile.nativeLanguage,
  foreignLanguages: profile.foreignLanguages,
});

/**
 * A duration, edited in minutes and reported in seconds.
 *
 * A stepper rather than a free text field: every one of these settings has a
 * sensible range and a sensible step, and a parent typing `600` when they meant
 * `60` is a mistake the control should not make possible.
 */
const MinuteRow = ({
  label,
  detail,
  valueSeconds,
  minMinutes,
  maxMinutes,
  onChange,
}: {
  label: string;
  detail: string;
  valueSeconds: number;
  minMinutes: number;
  maxMinutes: number;
  onChange: (seconds: number) => void;
}) => {
  const { t } = useTranslation();
  const minutes = toMinutes(valueSeconds);

  return (
    <Row label={label} detail={detail}>
      <Stepper
        value={minutes}
        display={`${minutes} ${t('unit.minuteShort')}`}
        min={minMinutes}
        max={maxMinutes}
        step={MINUTE_STEP}
        onChange={(next) => onChange(toSeconds(next))}
        decreaseLabel={t('screenTime.decrease', { setting: label })}
        increaseLabel={t('screenTime.increase', { setting: label })}
      />
    </Row>
  );
};

export const ScreenTimePage = () => {
  const { t } = useTranslation();
  // Typed rather than indexed: an untyped `useParams()` returns an index
  // signature, which `noPropertyAccessFromIndexSignature` and Biome's
  // `useLiteralKeys` then disagree about. Naming the parameter settles it.
  const parameters = useParams<{ childId: string }>();
  const parsedChildId = childIdSchema.safeParse(parameters.childId);

  return parsedChildId.success ? (
    <ScreenTimeEditor childId={parsedChildId.data} />
  ) : (
    <AppShell title={t('screenTime.title')}>
      <p className="help err" role="alert">
        {t('error.CHILD_NOT_FOUND')}
      </p>
    </AppShell>
  );
};

const ScreenTimeEditor = ({ childId }: { childId: ChildId }) => {
  const { t } = useTranslation();
  const { resource, replace } = useResource(`child:${childId}`, () =>
    apiClient.children.get({ childId }),
  );

  const [draft, setDraft] = useState<Draft | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingHours, setIsEditingHours] = useState(false);
  const [hasJustSaved, setHasJustSaved] = useState(false);

  useEffect(() => {
    if (resource.status === 'ready') {
      setDraft(toDraft(resource.data.profile));
      setErrorKey(null);
    }
  }, [resource.status, resource.data]);

  useEffect(() => {
    if (!hasJustSaved) {
      return;
    }
    const timer = setTimeout(() => setHasJustSaved(false), SAVED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [hasJustSaved]);

  if (resource.status === 'failed') {
    return (
      <AppShell title={t('screenTime.title')}>
        <p className="help err" role="alert">
          {t(toErrorKey(resource.error))}
        </p>
      </AppShell>
    );
  }

  if (resource.status === 'loading' || draft === null) {
    return (
      <AppShell title={t('screenTime.title')}>
        <p className="t-meta">{t('common.loading')}</p>
      </AppShell>
    );
  }

  const loaded = resource.data;
  const saved = toDraft(loaded.profile);
  const isDirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const setBudget = (change: Partial<ScreenTimeBudget>) => {
    setDraft({ ...draft, budget: { ...draft.budget, ...change } });
  };

  const setTypeBudget = (contentType: ContentType, seconds: number) => {
    setBudget({
      perContentTypeSeconds: { ...draft.budget.perContentTypeSeconds, [contentType]: seconds },
    });
  };

  const discard = () => {
    setDraft(saved);
    setErrorKey(null);
  };

  const save = async () => {
    setErrorKey(null);
    setIsSaving(true);

    try {
      const updated = await apiClient.children.updateProfile({ childId, profile: draft });
      replace(updated);
      setHasJustSaved(true);
    } catch (error) {
      setErrorKey(toErrorKey(error));
    } finally {
      setIsSaving(false);
    }
  };

  // Types the parent already has a budget for stay visible even when this
  // screen does not offer them as a default row; hiding a live setting would
  // make it uneditable and invisible at once.
  const shownTypes: ContentType[] = [
    ...EDITABLE_TYPES,
    ...(Object.keys(draft.budget.perContentTypeSeconds) as ContentType[]).filter(
      (contentType) => !EDITABLE_TYPES.includes(contentType),
    ),
  ];

  return (
    <AppShell
      title={loaded.child.name}
      subtitle={t('child.subtitle', {
        ageBand: t(`ageBand.${loaded.child.ageBand}`),
        language: t(`language.${loaded.profile.nativeLanguage}`),
      })}
    >
      <div className="child-settings">
        <p className="save-note">
          <Icon name="check" small />
          {t('child.saveNote')}
        </p>

        <div className="settings-grid">
          <Card title={t('screenTime.title')}>
            <MinuteRow
              label={t('screenTime.dailyBudget')}
              detail={t('screenTime.dailyBudgetDesc')}
              valueSeconds={draft.budget.dailyTotalSeconds}
              onChange={(seconds) => setBudget({ dailyTotalSeconds: seconds })}
              minMinutes={5}
              maxMinutes={480}
            />
            <MinuteRow
              label={t('screenTime.maxSession')}
              detail={t('screenTime.maxSessionDesc')}
              valueSeconds={draft.budget.maxSessionSeconds}
              onChange={(seconds) => setBudget({ maxSessionSeconds: seconds })}
              minMinutes={5}
              maxMinutes={240}
            />
            <MinuteRow
              label={t('screenTime.restPeriod')}
              detail={t('screenTime.restPeriodDesc')}
              valueSeconds={draft.budget.restPeriodSeconds}
              onChange={(seconds) => setBudget({ restPeriodSeconds: seconds })}
              minMinutes={0}
              maxMinutes={240}
            />
          </Card>

          <Card
            title={t('screenTime.allowedHours')}
            meta={t('child.hoursMeta')}
            aside={
              <Button
                small
                variant="ghost"
                aria-expanded={isEditingHours}
                onClick={() => setIsEditingHours(!isEditingHours)}
              >
                {t(isEditingHours ? 'child.hoursDone' : 'child.edit')}
              </Button>
            }
          >
            <WeekStrip schedule={draft.schedule} />
            {isEditingHours ? (
              <ScheduleEditor
                schedule={draft.schedule}
                onChange={(schedule: Schedule) => setDraft({ ...draft, schedule })}
              />
            ) : null}
          </Card>

          <Card title={t('screenTime.typeBudgets')} meta={t('child.typeBudgetsMeta')}>
            {shownTypes.map((contentType) => (
              <MinuteRow
                key={contentType}
                label={t(`contentType.${contentType}`)}
                detail={t(`contentTypeDesc.${contentType}`)}
                valueSeconds={draft.budget.perContentTypeSeconds[contentType] ?? 0}
                onChange={(seconds) => setTypeBudget(contentType, seconds)}
                minMinutes={0}
                maxMinutes={480}
              />
            ))}
          </Card>

          <OutcomeBlock
            childName={loaded.child.name}
            budget={draft.budget}
            schedule={draft.schedule}
          />

          {/*
           * The switch that makes the rule exist.
           *
           * It was stored, carried in the snapshot and reachable from nowhere,
           * which is the worst of the three states a feature can be in: built,
           * and impossible to ask for.
           */}
          <Card
            title={t('screenTime.movementBreaks')}
            meta={t('child.movementBreaksMeta')}
            aside={
              <Switch
                label={t('screenTime.movementBreaks')}
                checked={draft.movementBreaksEnabled}
                onChange={(movementBreaksEnabled) => setDraft({ ...draft, movementBreaksEnabled })}
              />
            }
          >
            {/*
             * Consent to a camera, asked before there is one.
             *
             * The signals do not exist yet — the tablet reports that it cannot
             * see and the rule treats that as unknown — and the switch is here
             * anyway. A consent control added *after* a camera ships is a
             * control somebody has already been watched without.
             */}
            <Row label={t('screenTime.activeViewing')} detail={t('child.activeViewingDetail')}>
              <Switch
                label={t('screenTime.activeViewing')}
                checked={draft.activeViewingEnabled}
                onChange={(activeViewingEnabled) => setDraft({ ...draft, activeViewingEnabled })}
              />
            </Row>
          </Card>

          {/*
           * The too-close threshold, a centimetre at a time.
           *
           * The estimate leans one way or the other for a particular child — it
           * assumes a toddler's pupil gap, and faces differ — and the parent is
           * the one who can see whether the warning comes too early or too
           * late. A fine step lets them correct for that without a new build.
           * The value is shown as "about": it is a setting, not a measurement
           * (`SPEC.md` §23).
           */}
          <Card
            title={t('screenTime.distanceWarning')}
            meta={t('child.distanceMeta')}
            aside={
              <Switch
                label={t('screenTime.distanceWarning')}
                checked={draft.distanceWarningEnabled}
                onChange={(distanceWarningEnabled) =>
                  setDraft({ ...draft, distanceWarningEnabled })
                }
              />
            }
          >
            {draft.distanceWarningEnabled ? (
              <div className="sub-row row-wrap">
                <Row
                  label={t('child.distance')}
                  detail={t('child.distanceRange', {
                    min: DISTANCE_WARNING_CENTIMETRES.min,
                    max: DISTANCE_WARNING_CENTIMETRES.max,
                  })}
                >
                  <Stepper
                    value={draft.distanceWarning.tooCloseCentimetres}
                    display={t('screenTime.distanceAbout', {
                      centimetres: draft.distanceWarning.tooCloseCentimetres,
                    })}
                    min={DISTANCE_WARNING_CENTIMETRES.min}
                    max={DISTANCE_WARNING_CENTIMETRES.max}
                    onChange={(tooCloseCentimetres) =>
                      setDraft({ ...draft, distanceWarning: { tooCloseCentimetres } })
                    }
                    decreaseLabel={t('screenTime.decrease', { setting: t('child.distance') })}
                    increaseLabel={t('screenTime.increase', { setting: t('child.distance') })}
                  />
                </Row>
                <p className="help">{t('child.distanceHelp')}</p>
              </div>
            ) : null}
          </Card>

          <ChildLanguages
            languages={{
              nativeLanguage: draft.nativeLanguage,
              foreignLanguages: draft.foreignLanguages,
            }}
            onChange={(languages) => setDraft({ ...draft, ...languages })}
          />

          <Card className="span-2">
            <div className="privacy">
              <span className="felt-sq sage">
                <Icon name="lock" />
              </span>
              <p>
                <b>{t('child.privacyTitle')}</b> {t('child.privacyNote')}
              </p>
            </div>
          </Card>
        </div>

        {isDirty ? (
          <div className="save-bar">
            {errorKey === null ? (
              <p className="save-bar-text">{t('child.unsaved')}</p>
            ) : (
              <p className="save-bar-text err" role="alert">
                {t(errorKey)}
              </p>
            )}
            <div className="save-bar-actions">
              <Button variant="ghost" disabled={isSaving} onClick={discard}>
                {t('child.discard')}
              </Button>
              <Button variant="primary" disabled={isSaving} onClick={() => void save()}>
                {t(isSaving ? 'common.saving' : 'common.save')}
              </Button>
            </div>
          </div>
        ) : null}

        {hasJustSaved ? (
          <p className="toast show" role="status">
            <Icon name="check" small />
            {t('child.saved')}
          </p>
        ) : null}
      </div>
    </AppShell>
  );
};
